import React, { useState, useCallback, useRef } from 'react';
import { leadsApi } from '../../services/api';
import { UploadIcon, CloseIcon } from '../../utils/icons';
import Modal from '../../components/Modal';
import './ExcelUploadModal.css';

const COLUMNS = ['name', 'phone', 'email', 'budget', 'address', 'project_name', 'type', 'remark'];
const COLUMN_LABELS = {
  name: 'Name', phone: 'Phone', email: 'Email', budget: 'Budget',
  address: 'Address', project_name: 'Project', type: 'Type', remark: 'Remark',
};

// Page-specific icons (used only here)
function FileIcon() { return <svg width="32" height="32" viewBox="0 0 24 24" fill="none"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>; }
function DownloadSmIcon() { return <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M8 2v8M5 7l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 13h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>; }

export default function ExcelUploadModal({ onClose, onImported }) {
  const [step, setStep] = useState('upload'); // upload | preview | importing | done
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);

  const [preview, setPreview] = useState(null);
  const [activeTab, setActiveTab] = useState('valid');
  const [result, setResult] = useState(null);

  const inputRef = useRef(null);

  // ── File selection ─────────────────────────────────────────────────────

  const handleFile = useCallback((f) => {
    setError('');
    if (!f.name.endsWith('.xlsx')) {
      setError('Only .xlsx files are supported.');
      return;
    }
    setFile(f);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
  }, [handleFile]);

  const handleDragOver = useCallback((e) => { e.preventDefault(); setDragActive(true); }, []);
  const handleDragLeave = useCallback(() => setDragActive(false), []);

  // ── Template download ──────────────────────────────────────────────────

  const handleDownloadTemplate = async () => {
    try {
      const res = await leadsApi.excelTemplate();
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'lead-import-template.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download template.');
    }
  };

  // ── Upload & preview ───────────────────────────────────────────────────

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const res = await leadsApi.excelPreview(file);
      setPreview(res.data);
      setStep('preview');
      setActiveTab(res.data.valid_rows.length > 0 ? 'valid' : res.data.invalid_rows.length > 0 ? 'invalid' : 'duplicate');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to parse file.');
    } finally {
      setUploading(false);
    }
  };

  // ── Confirm import ─────────────────────────────────────────────────────

  const handleConfirm = async () => {
    setStep('importing');
    setError('');
    try {
      const res = await leadsApi.excelConfirm(preview.valid_rows);
      setResult(res.data);
      setStep('done');
    } catch (err) {
      setError(err.response?.data?.detail || 'Import failed.');
      setStep('preview');
    }
  };

  // ── Render helpers ─────────────────────────────────────────────────────

  const renderUploadStep = () => (
    <>
      <div className="modal-body" style={{ padding: '20px 24px' }}>
        <div
          className={`eu-dropzone ${dragActive ? 'eu-dropzone--active' : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => inputRef.current?.click()}
        >
          <div className="eu-dropzone-icon"><FileIcon /></div>
          <p className="eu-dropzone-text">
            {file ? '' : 'Drag & drop your Excel file here, or click to browse'}
          </p>
          <p className="eu-dropzone-hint">.xlsx files only, max 500 rows</p>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx"
            style={{ display: 'none' }}
            onChange={(e) => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }}
          />
          {file && (
            <div className="eu-file-info">
              <span>{file.name}</span>
              <button className="eu-file-remove" onClick={(e) => { e.stopPropagation(); setFile(null); }}>
                <CloseIcon />
              </button>
            </div>
          )}
        </div>
        <button className="eu-template-link" onClick={(e) => { e.stopPropagation(); handleDownloadTemplate(); }}>
          <DownloadSmIcon /> Download template
        </button>
        {error && <div className="eu-error" style={{ marginTop: 12 }}>{error}</div>}
      </div>
      <div className="modal-footer" style={{ padding: '16px 24px', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={handleUpload} disabled={!file || uploading}>
          {uploading ? 'Parsing…' : 'Upload & Preview'}
        </button>
      </div>
    </>
  );

  const renderPreviewTable = (rows, type) => {
    if (rows.length === 0) return <div className="eu-empty">No {type} rows.</div>;

    return (
      <div className="eu-table-wrap">
        <table className="eu-table">
          <thead>
            <tr>
              {type !== 'valid' && <th>Row</th>}
              {COLUMNS.map((col) => <th key={col}>{COLUMN_LABELS[col]}</th>)}
              {type === 'invalid' && <th>Errors</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const data = type === 'valid' ? row : row.data;
              return (
                <tr key={i}>
                  {type !== 'valid' && <td className="eu-row-num">{row.row_number}</td>}
                  {COLUMNS.map((col) => <td key={col}>{data[col] || '—'}</td>)}
                  {type === 'invalid' && (
                    <td className="eu-row-errors">{row.errors.join(' ')}</td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderPreviewStep = () => {
    const { valid_rows, invalid_rows, duplicate_rows, summary } = preview;
    return (
      <>
        <div className="modal-body" style={{ padding: '20px 24px' }}>
          {error && <div className="eu-error">{error}</div>}
          <div className="eu-filter-bar">
            <span className="eu-filter-stat eu-filter-stat--total">{summary.total} total</span>
            <button className={`eu-filter-stat eu-filter-stat--valid ${activeTab === 'valid' ? 'eu-filter-stat--active' : ''}`} onClick={() => setActiveTab('valid')}>
              {summary.valid} valid
            </button>
            <button className={`eu-filter-stat eu-filter-stat--invalid ${activeTab === 'invalid' ? 'eu-filter-stat--active' : ''}`} onClick={() => setActiveTab('invalid')}>
              {summary.invalid} invalid
            </button>
            <button className={`eu-filter-stat eu-filter-stat--duplicate ${activeTab === 'duplicate' ? 'eu-filter-stat--active' : ''}`} onClick={() => setActiveTab('duplicate')}>
              {summary.duplicate} duplicate
            </button>
          </div>
          {activeTab === 'valid' && renderPreviewTable(valid_rows, 'valid')}
          {activeTab === 'invalid' && renderPreviewTable(invalid_rows, 'invalid')}
          {activeTab === 'duplicate' && renderPreviewTable(duplicate_rows, 'duplicate')}
        </div>
        <div className="modal-footer" style={{ padding: '16px 24px', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleConfirm} disabled={valid_rows.length === 0}>
            Import {valid_rows.length} Lead{valid_rows.length !== 1 ? 's' : ''}
          </button>
        </div>
      </>
    );
  };

  const renderImportingStep = () => (
    <div className="modal-body">
      <div className="eu-progress">
        <div className="eu-progress-spinner" />
        <p className="eu-progress-text">Importing {preview?.valid_rows.length} leads…</p>
      </div>
    </div>
  );

  const renderDoneStep = () => (
    <>
      <div className="modal-body">
        <div className="eu-result">
          <div className="eu-result-icon">{result.failed_count === 0 ? '✓' : '⚠'}</div>
          <h3 className="eu-result-title">
            {result.created_count} lead{result.created_count !== 1 ? 's' : ''} imported
          </h3>
          {result.failed_count > 0 && (
            <p className="eu-result-detail">{result.failed_count} row{result.failed_count !== 1 ? 's' : ''} failed to import.</p>
          )}
        </div>
      </div>
      <div className="modal-footer" style={{ padding: '16px 24px', display: 'flex', justifyContent: 'flex-end' }}>
        <button className="btn btn-primary btn-sm" onClick={() => { onImported(); }}>
          Close
        </button>
      </div>
    </>
  );

  return (
    <Modal title="Import Leads from Excel" size="lg" onClose={step === 'importing' ? () => {} : onClose}>
      {step === 'upload' && renderUploadStep()}
      {step === 'preview' && renderPreviewStep()}
      {step === 'importing' && renderImportingStep()}
      {step === 'done' && renderDoneStep()}
    </Modal>
  );
}
